/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

var FabricDirectory = class extends CustomHTMLElement {
  connectedCallback() {
    const checkboxes = Array.from(this.querySelectorAll('[type="checkbox"]')),
      checkboxName = "swatch-toggle",
      header = this.querySelector(".fabric-directory__header"),
      swatches = Array.from(this.getElementsByClassName("collection__swatch")),
      label = this.querySelector(".fabric-directory__swatch-toggle-label span"),
      sticky = this.querySelector(".fabric-directory__header-bottom"),
      list = this.querySelector(".list-collections"),
      categories = Array.from(this.querySelectorAll(".category"));

    this.header = header;
    this.sticky = sticky;
    this.checkbox = checkboxes.find((input) => input.name === checkboxName);
    this.swatches = swatches;
    this.label = label;
    this.header.style.top = `calc((var(--header-height, 0px) + ${this.sticky.offsetHeight}px + 2rem) - ${this.header.offsetHeight}px)`;
    this.list = list;
    this.checkbox?.addEventListener(
      "change",
      this._synchronizeProperties.bind(this)
    );
    this.categories = categories;
    window.addEventListener("resize", this._fillList.bind(this));
    this._synchronizeProperties();
    this._fillList();
  }
  _synchronizeProperties() {
    this.swatches.forEach((swatch) => {
      // swatch.style.display = this.checkbox.checked ? "block" : "none";
      swatch.classList.toggle("js:hidden", !this.checkbox.checked);
    });
    this.label.textContent = this.checkbox.checked
      ? "Display as list"
      : "Display as grid";
    this.list.classList.toggle(
      "list-collections--list",
      !this.checkbox.checked
    );
    this.list.classList.toggle("list-collections--grid", this.checkbox.checked);
  }
  _fillList() {
    const numColumns = this.offsetWidth < 741 ? 2 : 4;
    const collections = Array.from(
      this.querySelectorAll(".category__collections")
    );
    collections.forEach((collection) => {
      const childCount = collection.childElementCount;
      if (childCount % numColumns !== 0) {
        for (let i = 0; i < numColumns - (childCount % numColumns); i++) {
          let child = collection.appendChild(document.createElement("div"));
          child.className = "category__collection grid-hide";
          child.innerHTML = "&nbsp;";
        }
      }
    });

    this.categories.forEach((category) => {
      category.style.scrollMarginTop = `calc(var(--header-height, 0px) + ${this.sticky.offsetHeight}px + 3rem)`;
    });
  }
};
window.customElements.define("fabric-directory", FabricDirectory);

var ProductDescriptionCollapse = class extends CustomHTMLElement {
  connectedCallback() {
    this._collapseDescription();
  }
  _truncateString(str, maxLength) {
    if (str.length > maxLength) {
      return (
        str.slice(0, maxLength - 3) +
        "..." +
        '&nbsp;<span class="read-more">Read more</span>'
      );
    }
    return str;
  }
  _collapseDescription() {
    const el = document.createElement("div");
    const numChars = 100;
    el.innerHTML = this.dataset.description;
    this.innerHTML = this._truncateString(el.innerText, numChars);
    this.querySelector(".read-more")?.addEventListener(
      "click",
      this._expandDescription.bind(this)
    );
  }
  _expandDescription() {
    this.innerHTML =
      this.dataset.description + '<span class="read-less">Read less</span>';
    this.querySelector(".read-less")?.addEventListener(
      "click",
      this._collapseDescription.bind(this)
    );
  }
};
window.customElements.define(
  "product-description-collapse",
  ProductDescriptionCollapse
);

var FreeSample = class extends CustomHTMLElement {
  static get observedAttributes() {
    return ["aria-busy"];
  }
  constructor() {
    super();
    this.sampleVariantId =
      window?.themeVariables?.settings?.freeSampleProduct?.variants?.[0]?.id ??
      null;
  }
  connectedCallback() {
    this._prepareButton();
    document
      .getElementById(this.getAttribute("form-id"))
      ?.addEventListener("variant:changed", this._onVariantChanged.bind(this));
    this.addEventListener("click", this._addSampleToCart.bind(this));
    this.variant = JSON.parse(this.dataset.variantJson);
  }
  _onVariantChanged(event) {
    this.variant = event.detail.variant;
    this.dataset.variantJson = JSON.stringify(event.detail.variant);
    this.dataset.productVariant = this.variant.title;
    this.dataset.variantSku = this.variant.sku;
  }
  async _addSampleToCart(event) {
    event.preventDefault();

    this.setAttribute("disabled", "disabled");
    this.setAttribute("aria-busy", "true");

    const valid = await this._validate();

    if (valid?.success) {
      const form = new FormData();
      form.append("sections", ["mini-cart"]);
      form.append("quantity", 1);
      form.append("id", this.sampleVariantId);
      form.append("properties[Product Name]", this.dataset.productName);
      form.append("properties[Variant Name]", this.dataset.productVariant);
      form.append("properties[Variant SKU]", this.dataset.variantSku);

      const response = await fetch(
        `${window.themeVariables.routes.cartAddUrl}.js`,
        {
          body: form,
          method: "POST",
          headers: {
            "X-Requested-With": "XMLHttpRequest",
            // This is needed for the endpoint to properly return 422
          },
        }
      );

      this.removeAttribute("disabled");
      this.removeAttribute("aria-busy");

      const responseJson = await response.json();
      if (response.ok) {
        if (
          window.themeVariables.settings.cartType === "page" ||
          window.themeVariables.settings.pageType === "cart"
        ) {
          return (window.location.href = `${Shopify.routes.root}cart`);
        }
        this.dispatchEvent(
          new CustomEvent("variant:added", {
            bubbles: true,
            detail: {
              variant: responseJson.hasOwnProperty("items")
                ? responseJson["items"][0]
                : responseJson,
            },
          })
        );
        fetch(`${window.themeVariables.routes.cartUrl}.js`).then(
          async (response2) => {
            const cartContent = await response2.json();
            document.documentElement.dispatchEvent(
              new CustomEvent("cart:updated", {
                bubbles: true,
                detail: {
                  cart: cartContent,
                },
              })
            );
            cartContent["sections"] = responseJson["sections"];
            document.documentElement.dispatchEvent(
              new CustomEvent("cart:refresh", {
                bubbles: true,
                detail: {
                  cart: cartContent,
                  openMiniCart:
                    window.themeVariables.settings.cartType === "drawer" &&
                    this.closest(".drawer") === null,
                },
              })
            );
          }
        );
      }
      this.dispatchEvent(
        new CustomEvent("cart-notification:show", {
          bubbles: true,
          cancelable: true,
          detail: {
            status: response.ok ? "success" : "error",
            error: responseJson["description"] || "",
          },
        })
      );
    } else {
      this.removeAttribute("disabled");
      this.removeAttribute("aria-busy");

      this.dispatchEvent(
        new CustomEvent("cart-notification:show", {
          bubbles: true,
          cancelable: true,
          detail: {
            status: "error",
            error: valid?.error,
          },
        })
      );
    }
  }
  async _validate() {
    const sampleVariantId =
      window?.themeVariables?.settings?.freeSampleProduct?.variants?.[0]?.id ??
      null;
    const freeSamplesThreshold =
      window?.themeVariables?.settings?.freeSamplesThreshold ?? null;
    const cartMinimumAmountThreshold =
      window?.themeVariables?.settings?.cartMinimumAmountThreshold ?? null;
    const currencyFormat = window.themeVariables.settings.currencyCodeEnabled
      ? window.themeVariables.settings.moneyWithCurrencyFormat
      : window.themeVariables.settings.moneyFormat;

    if (
      sampleVariantId &&
      freeSamplesThreshold &&
      cartMinimumAmountThreshold &&
      currencyFormat
    ) {
      const response = await fetch(`/cart.js`);
      const cart = await response.json();

      const numSamples = cart?.items?.reduce((a, item) => {
        a += item?.variant_id === sampleVariantId ? item?.quantity : 0;
        return a;
      }, 0);
      const allSamples =
        cart?.items?.every((item) => item?.variant_id === sampleVariantId) &&
        numSamples === cart?.item_count;

      // check if sample is already in cart
      // check if more than 10 samples
      const exists = cart?.items?.some(
        (item) =>
          item?.variant_id === sampleVariantId &&
          item?.properties["Variant Name"] === this.variant?.title &&
          item?.properties["Variant SKU"] === this.variant?.sku &&
          item?.properties["Product Name"] === this.dataset.productName
      );
      const errMsg = exists
        ? "You can not checkout with more than 1 swatch per variant"
        : numSamples >= freeSamplesThreshold
        ? `You can not checkout with more than ${freeSamplesThreshold} swatches in your cart.`
        : "";

      if ((cart && exists) || numSamples >= freeSamplesThreshold) {
        // if cart.total_price < 2000 and num_samples != cart.item_count or num_samples > 10
        return { success: false, error: errMsg };
      }
      return { success: true };
    }
    return { success: true };
  }
  attributeChangedCallback(property, oldValue, newValue) {
    if (property === "aria-busy") {
      if (newValue === "true") {
        this._startTransition();
      } else {
        this._endTransition();
      }
    }
  }
};
Object.assign(FreeSample.prototype, LoaderButtonMixin);
window.customElements.define("free-sample", FreeSample), { extends: "button" };

// js/custom-element/section/product/quantity-total.js
var QuantityTotal = class extends HTMLElement {
  constructor() {
    super();
    this.quantityXPrice = this.querySelector("[price-x-quantity]");
    this.price = this.dataset.price;
    this.quantity = this.dataset.quantity;
    this.currencyFormat = window.themeVariables.settings.currencyCodeEnabled
      ? window.themeVariables.settings.moneyWithCurrencyFormat
      : window.themeVariables.settings.moneyFormat;
  }
  connectedCallback() {
    document
      .getElementById(this.getAttribute("form-id"))
      ?.addEventListener("variant:changed", this._onVariantChanged.bind(this));
    document
      .getElementById(this.getAttribute("form-id"))
      ?.addEventListener(
        "quantity:changed",
        this._onQuantityChanged.bind(this)
      );
  }
  _onVariantChanged(event) {
    this.price = event?.detail?.variant?.price;
    this.quantityXPrice.textContent = formatMoney(
      this.price * this.quantity,
      this.currencyFormat
    );
  }
  _onQuantityChanged(event) {
    this.quantity = event?.detail?.quantity;
    this.quantityXPrice.textContent = formatMoney(
      this.price * this.quantity,
      this.currencyFormat
    );
  }
};
window.customElements.define("quantity-total", QuantityTotal);

const checkForBulkVolDiscount = (e) => {
  if (window?.hulkapps?.discounts?.is_draft_order && window?.hulkappsCheckout) {
    e.preventDefault();
    window.hulkappsCheckout();
  } else {
    return true;
  }
};

/* ------------------------------------------------------------------------------------------------------------
 * PDP Breadcrumb
 * ------------------------------------------------------------------------------------------------------------
 */

window.BreadcrumbsModule = window.BreadcrumbsModule || {};
BreadcrumbsModule.localStorage = (function () {
  const BREADCRUMB = "breadcrumb";
  const ANCHOR_EL = `#${BREADCRUMB} .breadcrumb__item.placeholder`;
  const HIDDEN = "hidden";
  const CEIL = 2;
  let localArray = [];
  let HOME;

  const renderAnchor = function ({ title, url }) {
    return `<a href="${url}">${title}</a>`;
  };

  const hide = function (el, bool) {
    hidden = bool;
    el.ariaHidden = hidden.toString();
    if (bool) {
      el.classList.add(HIDDEN);
    } else {
      el.classList.remove(HIDDEN);
    }
  };

  const extractCollectionFromReferrer = function () {
    const referrer = document.referrer;
    const match = referrer.match(/\/collections\/([^/?]+)/);

    if (match) {
      return match[1].toUpperCase().replace(/-/g, "\u0020");
    }
    return "";
  };

  const render = function () {
    const el = document.querySelector(ANCHOR_EL);
    const breadcrumb = localStorage.getItem(BREADCRUMB);
    const localArray = JSON.parse(breadcrumb);

    if (!el) return;
    if (localArray?.length === CEIL) {
      const { title, url } = localArray?.[1];

      if (title !== document.title && url !== HOME && title) {
        hide(el, false);
        el.innerHTML = renderAnchor({
          title,
          url,
        });
      } else {
        hide(el, true);
        el.innerHTML = "";
      }
    } else {
      hide(el, true);
    }
  };

  const initialize = function () {
    HOME = `${window.location.origin}/`;
    const title = extractCollectionFromReferrer();
    const local = {
      title,
      url: window.location.href,
    };

    const breadcrumb = localStorage.getItem(BREADCRUMB);
    let array = JSON.parse(breadcrumb);
    if (!breadcrumb || array.length === 0) {
      localArray.push(local);
    } else {
      if (array?.length > 0 && array?.length < CEIL) {
        array.push(local);
        localArray = [...array];
      } else if (array?.length >= CEIL) {
        if (array?.[CEIL - 1].title !== local.title) {
          localArray = [array?.[1], local];
        } else {
          localArray = [...array];
        }
      }
    }
    localStorage.setItem(BREADCRUMB, JSON.stringify(localArray));
    render();
  };

  return {
    initialize,
  };
})();
document.addEventListener("DOMContentLoaded", function () {
  BreadcrumbsModule.localStorage.initialize();
});
